import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Award, BookOpen, Users, LogOut } from 'lucide-react';
import { toast } from 'sonner';

import QuizQuestion from '@/components/quiz/QuizQuestion';
import SequenceQuestion from '@/components/quiz/SequenceQuestion';
import ComplianceQuestion from '@/components/quiz/ComplianceQuestion';
import TicketSimulation from '@/components/quiz/TicketSimulation';
import QuizResults from '@/components/quiz/QuizResults';
import TrainingSelect from '@/components/quiz/TrainingSelect';
import TopicSelect from '@/components/quiz/TopicSelect';
import ThemeToggle from '@/components/ThemeToggle';

import { GENERAL_TOPICS } from '@/data/generalTopics';
import { computeScore } from '@/utils/quiz';
import {
  fetchQuestionCounts,
  fetchQuestions,
  fetchMixedGeneral,
} from '@/lib/questions';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';

export default function Home() {
  const { user, isAdmin, signOut } = useAuth();

  const [phase, setPhase] = useState('select'); // select | topic | quiz | results
  const [track, setTrack] = useState('report');
  const [topic, setTopic] = useState('report');
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [savedScoreId, setSavedScoreId] = useState(null);

  // Counts drive the track cards and topic picker before anything is fetched.
  const [counts, setCounts] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);

  useEffect(() => {
    let active = true;
    fetchQuestionCounts().then((c) => {
      if (active) setCounts(c);
    });
    return () => {
      active = false;
    };
  }, []);

  const topicsWithCounts = GENERAL_TOPICS.map((t) => ({
    ...t,
    count: counts?.byTopic?.[t.key] ?? 0,
  }));

  const startQuiz = (nextTrack, nextTopic, nextQuestions) => {
    if (!nextQuestions.length) {
      toast.error('No questions available', {
        description: 'Nothing has been published for this track yet.',
      });
      return;
    }
    setTrack(nextTrack);
    setTopic(nextTopic);
    setQuestions(nextQuestions);
    setAnswers({});
    setCurrentIndex(0);
    setSavedScoreId(null);
    setPhase('quiz');
  };

  const handleSelect = async (key) => {
    if (key === 'general') return setPhase('topic');

    setLoadingQuiz(true);
    const loaded = await fetchQuestions({ track: key });
    setLoadingQuiz(false);
    startQuiz(key, key, loaded);
  };

  const handleTopicSelect = async (topicKey) => {
    setLoadingQuiz(true);
    const loaded =
      topicKey === 'mixed'
        ? await fetchMixedGeneral()
        : await fetchQuestions({ track: 'general', topic: topicKey });
    setLoadingQuiz(false);
    startQuiz('general', topicKey, loaded);
  };

  const handleAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [questions[currentIndex].id]: value }));
  };

  const saveScore = async (finalAnswers) => {
    if (!user) return;

    const { correct, total, pct } = computeScore(questions, finalAnswers);

    const { data, error } = await supabase
      .from('training_scores')
      .insert({
        user_id: user.id,
        score: pct,
        correct_count: correct,
        total,
        track,
        topic,
      })
      .select('id')
      .single();

    if (error) {
      console.error('Saving score failed:', error.message);
      toast.error("Your score couldn't be saved", {
        description: 'The session still counts — try again from the leaderboard later.',
      });
      return;
    }

    setSavedScoreId(data.id);
  };

  const handleNext = () => {
    if (currentIndex === questions.length - 1) {
      setPhase('results');
      saveScore(answers);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleRestart = () => {
    setAnswers({});
    setCurrentIndex(0);
    setSavedScoreId(null);
    setPhase('select');
  };

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion?.id] ?? null;

  const renderQuestion = () => {
    const props = {
      question: currentQuestion,
      index: currentIndex,
      total: questions.length,
      answer: currentAnswer,
      onAnswer: handleAnswer,
      onNext: handleNext,
      onBack: handleBack,
    };

    if (currentQuestion.type === 'sequence') return <SequenceQuestion {...props} />;
    if (currentQuestion.type === 'compliance') return <ComplianceQuestion {...props} />;
    if (currentQuestion.type === 'ticket_simulation')
      return <TicketSimulation {...props} />;
    return <QuizQuestion {...props} />;
  };

  const spinner = (
    <div className="flex justify-center py-24">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="text-sm font-semibold tracking-tight text-foreground">
            Customer Support Training Arena
          </span>
          <div className="flex items-center gap-1">
            {/* KB Tests is now an admin review screen, not a track */}
            {isAdmin && (
              <Link
                to="/kb-tests"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                <span className="hidden md:inline">KB Review</span>
              </Link>
            )}
            <Link
              to="/team"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Users className="w-4 h-4" />
              <span className="hidden md:inline">Team</span>
            </Link>
            <Link
              to="/badges"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Award className="w-4 h-4" />
              <span className="hidden md:inline">Badges</span>
            </Link>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Trophy className="w-4 h-4" />
              <span className="hidden md:inline">Leaderboard</span>
            </Link>
            <ThemeToggle />
            <button
              type="button"
              onClick={signOut}
              title="Log out"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main>
        {loadingQuiz && spinner}

        {!loadingQuiz && phase === 'select' &&
          (counts === null ? (
            spinner
          ) : (
            <TrainingSelect
              reportCount={counts.report}
              generalCount={counts.generalTotal}
              monthlyCount={counts.monthly}
              onSelect={handleSelect}
            />
          ))}

        {!loadingQuiz && phase === 'topic' && (
          <TopicSelect
            topics={topicsWithCounts}
            mixedCount={Math.min(12, counts?.generalTotal ?? 0)}
            onSelect={handleTopicSelect}
            onBack={() => setPhase('select')}
          />
        )}

        {!loadingQuiz && phase === 'quiz' && currentQuestion && renderQuestion()}

        {!loadingQuiz && phase === 'results' && (
          <QuizResults
            questions={questions}
            answers={answers}
            onRestart={handleRestart}
            topicKey={topic}
            scoreId={savedScoreId}
          />
        )}
      </main>
    </div>
  );
}
